import { useTranslation } from 'react-i18next';
import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import ScrollReveal from '@/components/base/ScrollReveal';

const STEPS = [
  { key: 'eco1', icon: 'ri-focus-3-line' },
  { key: 'eco2', icon: 'ri-user-search-line' },
  { key: 'eco3', icon: 'ri-advertisement-line' },
  { key: 'eco4', icon: 'ri-global-line' },
  { key: 'eco5', icon: 'ri-pages-line' },
  { key: 'eco6', icon: 'ri-settings-3-line' },
  { key: 'eco7', icon: 'ri-database-2-line' },
  { key: 'eco9', icon: 'ri-shopping-bag-3-line' },
  { key: 'eco10', icon: 'ri-star-line' },
];

function EcosystemStep({ step, index, total }: { step: typeof STEPS[0]; index: number; total: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px 0px -80px 0px', amount: 0.5 });
  const { t } = useTranslation();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col items-center"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={isInView ? { scale: 1 } : { scale: 0 }}
        transition={{ duration: 0.5, delay: index * 0.12 + 0.2, ease: 'easeOut' }}
        className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-3 ${
          index === 0 ? 'bg-[#E53935] text-white shadow-[0_4px_20px_rgba(229,57,53,0.3)]' :
          'bg-white border border-gray-200 text-[#111111] shadow-[0_2px_8px_rgba(0,0,0,0.04)]'
        }`}
      >
        <i className={`${step.icon} text-lg`}></i>
      </motion.div>
      <span className="text-[#111111] text-xs md:text-sm font-semibold text-center whitespace-nowrap">
        {t(`trafficMaster.${step.key}`)}
      </span>
      {index < total - 1 && (
        <div className="hidden md:block mt-2">
          <i className="ri-arrow-down-line text-gray-300 text-sm"></i>
        </div>
      )}
    </motion.div>
  );
}

export default function Ecosystem() {
  const { t } = useTranslation();

  return (
    <section className="py-20 md:py-32 bg-[#F7F8FA]">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <ScrollReveal>
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white text-[#C8A24A] text-xs font-semibold tracking-wider uppercase mb-5 border border-[#C8A24A]/20">
              {t('trafficMaster.ecosystemTag')}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-[#111111] leading-tight">
              {t('trafficMaster.ecosystemTitle')}{' '}
              <span className="text-[#E53935]">{t('trafficMaster.ecosystemTitleHighlight')}</span>
            </h2>
          </div>
        </ScrollReveal>

        <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-x-6 gap-y-10">
          {STEPS.map((step, i) => (
            <EcosystemStep key={step.key} step={step} index={i} total={STEPS.length} />
          ))}
        </div>

        <div className="md:hidden grid grid-cols-2 gap-6">
          {STEPS.map((step, i) => (
            <EcosystemStep key={step.key} step={step} index={i} total={STEPS.length} />
          ))}
        </div>
      </div>
    </section>
  );
}